import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getSubscriptionByEmail, type SubscriptionStatus } from "@/lib/subscription";

const DISMISS_KEY = "cp_sub_expiry_dismissed";

/**
 * Surfaces a thin warning banner when the signed-in user's subscription
 * has been cancelled and access lapses within ≤ 3 days. We deliberately
 * skip healthy auto-renewing subscriptions — a "your card will be billed
 * in 3 days" nudge would be banner-noise. Dismissible per-session so
 * users who already know don't have to look at it on every nav.
 */
export const SubscriptionExpiryBanner = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    const load = async (email: string | null) => {
      if (!email) {
        if (!cancelled) setSub(null);
        return;
      }
      const next = await getSubscriptionByEmail(email);
      if (!cancelled) setSub(next);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      void load(session?.user?.email ?? null);
    });
    const { data } = supabase.auth.onAuthStateChange((_e, session) => {
      void load(session?.user?.email ?? null);
    });
    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, []);

  if (dismissed || !sub) return null;
  if (!sub.cancelAtPeriodEnd) return null;
  if (!sub.current_period_end) return null;
  const ms = new Date(sub.current_period_end).getTime() - Date.now();
  if (ms <= 0 || ms > 3 * 24 * 60 * 60 * 1000) return null;

  const endDate = new Date(sub.current_period_end);
  const dateLabel = endDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // non-fatal
    }
  };

  return (
    <div
      role="status"
      className="border-b border-secondary/40 bg-secondary/20 px-4 py-2 text-center text-xs text-foreground"
    >
      <div className="mx-auto flex max-w-3xl items-center justify-center gap-3">
        <span>
          Subscription ends <strong>{dateLabel}</strong> — you'll lose access to
          paid features.{" "}
          <button
            type="button"
            onClick={() => navigate("/account")}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Manage
          </button>
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="rounded-md p-1 text-muted-foreground transition-smooth hover:bg-background/60 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
